/**
 * Global, Company and Site settings (PRD 6.1), and who does each department's
 * work at each site.
 *
 * A blank is not a setting: it inherits from the nearest level above that has
 * one, and the screen says which level that is and what it holds, beside the
 * blank. Holidays are the exception the PRD makes -- a site's holidays are
 * added to its company's and Global's, not instead of them -- and the screen
 * says that too. The resolution is core's `resolveOne()`, the same function
 * the object uses to work out a deadline.
 */

import { useMemo, useState, type FormEvent } from "react";
import {
  type DepartmentView,
  type DepartmentsResponse,
  hourText,
  PAID_PLANS,
  type Plan,
  planOf,
  PLANS,
  type PlanUse,
  PLATFORM_DEFAULTS,
  resolveOne,
  STATE_NAMES,
  type LevelView,
  type SettingsLevel,
  type SetupResponse,
  type Weekday,
} from "@utuwrk/core";
import { sentence } from "../lib/api.js";
import { useOrg } from "../lib/org.js";
import { WEEKDAYS, dayText } from "../lib/words.js";
import { Link, Loading, PageHeader, Problem, useLoad } from "./bits.js";

const ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(key: string): string[] }).supportedValuesOf("timeZone");
  } catch {
    return [];
  }
})();

/** The levels above this one, outermost first. */
function above(level: LevelView, levels: readonly LevelView[]): LevelView[] {
  const global = levels.find((candidate) => candidate.kind === "global");
  if (level.kind === "global" || !global) return [];
  if (level.kind === "company") return [global];
  const company = levels.find((candidate) => candidate.id === level.parentId);
  return company ? [global, company] : [global];
}

const show = (key: keyof SettingsLevel, value: unknown): string => {
  if (key === "workdays") return (value as Weekday[]).map((day) => WEEKDAYS[day]).join(", ");
  if (key === "deadlineUnit") return value === "calendar" ? "calendar days" : "business days";
  if (key === "hipaa") return value ? "on" : "off";
  if (key === "digestHour") return hourText(value as number);
  if (key === "jurisdiction") return value ? (STATE_NAMES[value as string] ?? String(value)) : "not set";
  if (key === "previewDepartments")
    return (value as string[]).length === 0 ? "nobody" : (value as string[]).join(", ");
  return String(value);
};

export function Settings() {
  const { api, org } = useOrg();
  const { data, setData, error } = useLoad(() => api.setup(), [api]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const admin = org.role === "admin";
  const staffer = admin || org.role === "hr";

  async function run(work: () => Promise<SetupResponse>): Promise<boolean> {
    setBusy(true);
    setProblem(null);
    try {
      setData(await work());
      return true;
    } catch (failure) {
      setProblem(sentence(failure));
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (!data && !error) return <Loading title="Settings" />;
  const levels = data?.levels ?? [];
  const companies = levels.filter((level) => level.kind === "company");

  return (
    <section className="page">
      <PageHeader
        title="Settings"
        meta="Set things once at the top, and only what is different below. A blank uses the level above."
        actions={
          admin ? (
            <Link to={{ name: "setup" }} className="button button-quiet">
              Setup wizard
            </Link>
          ) : null
        }
      />
      <Problem>{error ?? problem}</Problem>

      {admin ? <OrganizationName /> : null}

      {levels.map((level) => (
        <LevelEditor key={level.id} level={level} levels={levels} admin={admin} busy={busy} run={run} />
      ))}

      {admin ? <AddLevel companies={companies} busy={busy} run={run} /> : null}

      {staffer ? <Departments levels={levels} onRenamed={() => void run(() => api.setup())} /> : null}

      {data ? <StaffingEditor setup={data} editable={staffer} busy={busy} run={run} /> : null}

      {admin ? <Billing /> : null}

      {admin ? <MailPool /> : null}

      {admin ? <CloseOrganization /> : null}
    </section>
  );
}

/**
 * Closing the organisation (PRD 13.3): thirty days to export, then everything
 * deleted. Its name typed to confirm; reopening any time before then.
 */
function CloseOrganization() {
  const { api, org, refreshSession } = useOrg();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function act(work: () => Promise<unknown>) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      await refreshSession();
    } catch (failure) {
      setProblem(sentence(failure));
    } finally {
      setBusy(false);
    }
  }

  if (org.closesAt !== null)
    return (
      <div className="panel admin-panel">
        <h2 className="admin-heading">Closing</h2>
        <p>
          Everything in {org.name} is deleted on{" "}
          {dayText(new Date(org.closesAt).toISOString().slice(0, 10), true)}. Reopen it to keep working;
          nothing has been deleted yet.
        </p>
        <div className="action-row">
          <button
            className="button"
            type="button"
            disabled={busy}
            onClick={() => void act(() => api.reopen())}
          >
            Reopen {org.name}
          </button>
        </div>
        <Problem>{problem}</Problem>
      </div>
    );

  return (
    <details className="panel">
      <summary className="admin-heading">Close the organisation</summary>
      <form
        className="admin-panel"
        onSubmit={(event) => {
          event.preventDefault();
          void act(() => api.close(typed));
        }}
      >
        <p className="muted">
          For leaving utuwrk. Everybody keeps reading and exporting for 30 days, and the admins are mailed
          what to take; then every person, onboarding, offboarding, template and the audit log is deleted for
          good. You can reopen it until then.
        </p>
        <div className="admin-form">
          <label className="field">
            Type {org.name} to confirm
            <input value={typed} onChange={(event) => setTyped(event.target.value)} />
          </label>
          <button className="button button-danger" type="submit" disabled={busy || typed.trim() !== org.name}>
            Close {org.name}
          </button>
        </div>
        <Problem>{problem}</Problem>
      </form>
    </details>
  );
}

/** The organisation's own name, as everybody in it sees it in the header. */
function OrganizationName() {
  const { api, org, refreshSession } = useOrg();
  const [name, setName] = useState(org.name);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      setName((await api.rename(name)).name);
      await refreshSession();
    } catch (failure) {
      setProblem(sentence(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <h2 className="admin-heading">Organisation</h2>
      <form className="admin-form" onSubmit={submit}>
        <label className="field">
          Name
          <input value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <button className="button" type="submit" disabled={busy || !name.trim() || name.trim() === org.name}>
          Rename
        </button>
      </form>
      <Problem>{problem}</Problem>
    </div>
  );
}

/**
 * The departments the organisation keeps: what every picker offers. A rename
 * reaches people, staffing, templates and open work, so it says so before it
 * goes; archiving only takes one out of the pickers.
 */
function Departments({ levels, onRenamed }: { levels: LevelView[]; onRenamed: () => void }) {
  const { api, refreshPeople } = useOrg();
  const { data, setData, error } = useLoad(() => api.departments(), [api]);
  const [adding, setAdding] = useState("");
  const [addingAt, setAddingAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function run(work: () => Promise<DepartmentsResponse>, renamed = false): Promise<boolean> {
    setBusy(true);
    setProblem(null);
    try {
      setData(await work());
      await refreshPeople();
      if (renamed) onRenamed();
      return true;
    } catch (failure) {
      setProblem(sentence(failure));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const live = data?.departments.filter((department) => !department.archived) ?? [];
  const archived = data?.departments.filter((department) => department.archived) ?? [];

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Departments</h2>
      <p className="muted">
        Every place you choose a department offers these. Each is used from the whole organisation, or from
        one company or site down, and a company or site beneath that can switch it off.
      </p>
      <Problem>{error ?? problem}</Problem>
      <ul className="admin-log" aria-label="Departments">
        {live.map((department) => (
          <DepartmentRow
            key={department.name}
            department={department}
            levels={levels}
            editable={data?.canEdit ?? false}
            busy={busy}
            run={run}
          />
        ))}
      </ul>
      {data?.canEdit ? (
        <form
          className="admin-form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (await run(() => api.addDepartment(adding, addingAt || null))) setAdding("");
          }}
        >
          <label className="field">
            New department
            <input value={adding} onChange={(event) => setAdding(event.target.value)} placeholder="Quality" />
          </label>
          {levels.length > 1 ? (
            <label className="field">
              Used from
              <select value={addingAt} onChange={(event) => setAddingAt(event.target.value)}>
                <option value="">The whole organisation</option>
                {scopeLevels(levels).map((level) => (
                  <option key={level.id} value={level.id}>
                    {levelLabel(level, levels)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button className="button" type="submit" disabled={busy || !adding.trim()}>
            Add
          </button>
        </form>
      ) : null}
      {archived.length > 0 ? (
        <details>
          <summary>Archived: {archived.length}</summary>
          <ul className="admin-log" aria-label="Archived departments">
            {archived.map((department) => (
              <DepartmentRow
                key={department.name}
                department={department}
                levels={levels}
                editable={data?.canEdit ?? false}
                busy={busy}
                run={run}
              />
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

/** "3 people, 2 templates, 5 open cards", leaving out what is none. */
function departmentFacts(department: DepartmentView): string {
  const parts = [
    department.people ? `${department.people} ${department.people === 1 ? "person" : "people"}` : "",
    department.templates ? `${department.templates} template${department.templates === 1 ? "" : "s"}` : "",
    department.openCards ? `${department.openCards} open card${department.openCards === 1 ? "" : "s"}` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "Nothing in it yet";
}

/** Companies and sites: the levels a department can be used from (Global is the whole organisation). */
const scopeLevels = (levels: readonly LevelView[]): LevelView[] =>
  levels.filter((level) => level.kind !== "global");

/** A site is told by its company as well, so two companies with a Portland are not confused. */
function levelLabel(level: LevelView, levels: readonly LevelView[]): string {
  const parent = level.parentId ? levels.find((other) => other.id === level.parentId) : null;
  return parent ? `${parent.name}, ${level.name}` : level.name;
}

/** The levels at or beneath one, which may switch a department off: all of them when it is used everywhere. */
function beneath(level: string | null, levels: readonly LevelView[]): LevelView[] {
  return scopeLevels(levels).filter(
    (other) => level === null || other.id === level || other.parentId === level,
  );
}

/** "Used everywhere", or "Used from Portland down, off at Salem". */
function useText(department: DepartmentView, levels: readonly LevelView[]): string {
  const name = (id: string) => levels.find((level) => level.id === id)?.name ?? "a level that is gone";
  const from = department.level === null ? "Used everywhere" : `Used from ${name(department.level)} down`;
  return department.off.length ? `${from}, off at ${department.off.map(name).join(", ")}` : from;
}

function DepartmentRow({
  department,
  levels,
  editable,
  busy,
  run,
}: {
  department: DepartmentView;
  levels: LevelView[];
  editable: boolean;
  busy: boolean;
  run: (work: () => Promise<DepartmentsResponse>, renamed?: boolean) => Promise<boolean>;
}) {
  const { api } = useOrg();
  const [renaming, setRenaming] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [name, setName] = useState(department.name);
  const [from, setFrom] = useState<string>(department.level ?? "");
  const [off, setOff] = useState<string[]>(department.off);

  return (
    <li>
      <div className="admin-row">
        <strong>{department.name}</strong>
        {editable && !renaming && !placing ? (
          <span className="action-row">
            {!department.archived && levels.length > 1 ? (
              <button
                className="button button-quiet"
                type="button"
                onClick={() => {
                  setFrom(department.level ?? "");
                  setOff(department.off);
                  setPlacing(true);
                }}
              >
                Where it is used
              </button>
            ) : null}
            {!department.archived ? (
              <button className="button button-quiet" type="button" onClick={() => setRenaming(true)}>
                Rename
              </button>
            ) : null}
            <button
              className="button button-quiet"
              type="button"
              disabled={busy}
              onClick={() => void run(() => api.archiveDepartment(department.name, !department.archived))}
            >
              {department.archived ? "Bring back" : "Archive"}
            </button>
          </span>
        ) : null}
      </div>
      <span className="muted">
        {levels.length > 1 ? `${useText(department, levels)}. ` : ""}
        {departmentFacts(department)}
      </span>
      {placing ? (
        <form
          className="admin-form"
          onSubmit={async (event) => {
            event.preventDefault();
            const reach = new Set(beneath(from || null, levels).map((level) => level.id));
            const switched = off.filter((id) => reach.has(id));
            if (await run(() => api.setDepartmentUse(department.name, from || null, switched)))
              setPlacing(false);
          }}
        >
          <label className="field">
            Used from
            <select
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                setOff([]);
              }}
            >
              <option value="">The whole organisation</option>
              {scopeLevels(levels).map((level) => (
                <option key={level.id} value={level.id}>
                  {levelLabel(level, levels)}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="field">
            <legend>Switched off at</legend>
            {beneath(from || null, levels).map((level) => (
              <label key={level.id} className="check">
                <input
                  type="checkbox"
                  checked={off.includes(level.id)}
                  onChange={(event) =>
                    setOff(event.target.checked ? [...off, level.id] : off.filter((id) => id !== level.id))
                  }
                />
                <span>{levelLabel(level, levels)}</span>
              </label>
            ))}
          </fieldset>
          <p className="muted">
            Switching it off hides it from that place&rsquo;s staffing and start forms, and from everything
            beneath it. People and cards already in it stay.
          </p>
          <span className="action-row">
            <button className="button" type="submit" disabled={busy}>
              Save
            </button>
            <button className="button button-quiet" type="button" onClick={() => setPlacing(false)}>
              Cancel
            </button>
          </span>
        </form>
      ) : null}
      {renaming ? (
        <form
          className="admin-form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (await run(() => api.renameDepartment(department.name, name), true)) setRenaming(false);
          }}
        >
          <label className="field">
            New name
            <input value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <span className="action-row">
            <button
              className="button"
              type="submit"
              disabled={busy || !name.trim() || name.trim() === department.name}
            >
              Rename everywhere
            </button>
            <button className="button button-quiet" type="button" onClick={() => setRenaming(false)}>
              Cancel
            </button>
          </span>
          <p className="muted">
            Its people, staffing, templates and open cards all move to the new name. Finished work keeps the
            old one.
          </p>
        </form>
      ) : null}
    </li>
  );
}

interface Props {
  busy: boolean;
  run: (work: () => Promise<SetupResponse>) => Promise<boolean>;
}

function LevelEditor({
  level,
  levels,
  admin,
  busy,
  run,
}: Props & { level: LevelView; levels: readonly LevelView[]; admin: boolean }) {
  const { api } = useOrg();
  const parents = useMemo(() => above(level, levels), [level, levels]);
  const [name, setName] = useState(level.name);
  const [timeZone, setTimeZone] = useState(level.settings.timeZone ?? "");
  const [workdays, setWorkdays] = useState<Weekday[] | null>(level.settings.workdays ?? null);
  const [holidays, setHolidays] = useState((level.settings.holidays ?? []).join("\n"));
  const [deadlineUnit, setDeadlineUnit] = useState(level.settings.deadlineUnit ?? "");
  const [digestHour, setDigestHour] = useState(
    level.settings.digestHour === undefined ? "" : String(level.settings.digestHour),
  );
  const [jurisdiction, setJurisdiction] = useState(level.settings.jurisdiction ?? "");
  const [preview, setPreview] = useState(
    level.settings.previewDepartments === undefined
      ? ""
      : level.settings.previewDepartments.length === 0
        ? "none"
        : level.settings.previewDepartments.join(", "),
  );
  const [hipaa, setHipaa] = useState(
    level.settings.hipaa === undefined ? "" : level.settings.hipaa ? "on" : "off",
  );

  /** What a blank here would come out as, and where from. */
  const inherited = (key: keyof SettingsLevel): string => {
    const found = resolveOne(
      parents.map((parent) => parent.settings),
      key,
    );
    if (found.from === -1) return `Default: ${show(key, PLATFORM_DEFAULTS[key])}`;
    return `From ${parents[found.from]!.name}: ${show(key, found.value)}`;
  };
  const inheritedHolidays = parents.flatMap((parent) => parent.settings.holidays ?? []);

  async function save(event: FormEvent) {
    event.preventDefault();
    const settings: SettingsLevel = {};
    if (timeZone.trim()) settings.timeZone = timeZone.trim();
    if (workdays) settings.workdays = workdays;
    const days = holidays
      .split(/[\s,]+/)
      .map((day) => day.trim())
      .filter(Boolean);
    if (days.length > 0) settings.holidays = days;
    if (deadlineUnit === "business" || deadlineUnit === "calendar") settings.deadlineUnit = deadlineUnit;
    if (hipaa) settings.hipaa = hipaa === "on";
    if (digestHour !== "") settings.digestHour = Number(digestHour);
    if (jurisdiction) settings.jurisdiction = jurisdiction;
    if (preview.trim())
      settings.previewDepartments =
        preview.trim().toLowerCase() === "none"
          ? []
          : preview
              .split(",")
              .map((name) => name.trim())
              .filter(Boolean);
    await run(() => api.saveLevel(level.id, { name, settings }));
  }

  const kindWord =
    level.kind === "global" ? "Every company and site" : level.kind === "company" ? "Company" : "Site";
  const toggle = (day: Weekday) => {
    const now = workdays ?? [];
    setWorkdays(now.includes(day) ? now.filter((other) => other !== day) : [...now, day].sort());
  };

  return (
    <details className={`panel level level-${level.kind}`} open={level.kind === "global"}>
      <summary className="level-summary">
        <span className="level-name">{level.name}</span>
        <span className="chip">{kindWord}</span>
      </summary>
      <form className="level-form" onSubmit={save}>
        <fieldset className="level-fields" disabled={!admin || busy}>
          {level.kind !== "global" ? (
            <label className="field">
              <span>Name</span>
              <input value={name} onChange={(event) => setName(event.target.value)} />
            </label>
          ) : null}

          <label className="field">
            <span>Time zone</span>
            <input
              list="zones"
              value={timeZone}
              onChange={(event) => setTimeZone(event.target.value)}
              placeholder={level.kind === "global" ? "America/New_York" : "Blank: the level above"}
            />
            {!timeZone.trim() ? <span className="inherit">{inherited("timeZone")}</span> : null}
          </label>

          <div className="field">
            <span>Working days</span>
            <label className="check">
              <input
                type="checkbox"
                checked={workdays === null}
                onChange={(event) => setWorkdays(event.target.checked ? null : [1, 2, 3, 4, 5])}
              />
              <span>{level.kind === "global" ? "Monday to Friday" : "Same as the level above"}</span>
            </label>
            {workdays === null ? (
              <span className="inherit">{inherited("workdays")}</span>
            ) : (
              <div className="weekdays">
                {WEEKDAYS.map((label, index) => (
                  <label key={label} className="day">
                    <input
                      type="checkbox"
                      checked={workdays.includes(index as Weekday)}
                      onChange={() => toggle(index as Weekday)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <label className="field">
            <span>Deadlines count</span>
            <select value={deadlineUnit} onChange={(event) => setDeadlineUnit(event.target.value as "")}>
              <option value="">
                {level.kind === "global" ? "Business days" : "Same as the level above"}
              </option>
              <option value="business">Business days</option>
              <option value="calendar">Calendar days (a site that runs every day)</option>
            </select>
            {!deadlineUnit ? <span className="inherit">{inherited("deadlineUnit")}</span> : null}
          </label>

          <label className="field">
            <span>Lateness summary goes at</span>
            <select value={digestHour} onChange={(event) => setDigestHour(event.target.value)}>
              <option value="">{level.kind === "global" ? "9am" : "Same as the level above"}</option>
              {Array.from({ length: 24 }, (_, hour) => (
                <option key={hour} value={hour}>
                  {hourText(hour)}
                </option>
              ))}
            </select>
            {digestHour === "" ? (
              <span className="inherit">{inherited("digestHour")}</span>
            ) : (
              <span className="inherit">On working days, in the time zone above.</span>
            )}
          </label>

          <label className="field">
            <span>State</span>
            <select value={jurisdiction} onChange={(event) => setJurisdiction(event.target.value)}>
              <option value="">{level.kind === "global" ? "Not set" : "Same as the level above"}</option>
              {Object.entries(STATE_NAMES).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
            {!jurisdiction ? (
              <span className="inherit">{inherited("jurisdiction")}</span>
            ) : (
              <span className="inherit">
                A departure's final pay card says what the law there requires. Set it on each site that
                differs.
              </span>
            )}
          </label>

          <label className="field">
            <span>Told about the week ahead</span>
            <input
              value={preview}
              onChange={(event) => setPreview(event.target.value)}
              placeholder={level.kind === "global" ? "IT, Facilities" : "Blank: the level above"}
            />
            {!preview.trim() ? (
              <span className="inherit">{inherited("previewDepartments")}</span>
            ) : (
              <span className="inherit">
                Their managers and whoever staffs them, by mail on the last working day of the week. Write
                "none" to mail nobody.
              </span>
            )}
          </label>

          <label className="field">
            <span>HIPAA</span>
            <select value={hipaa} onChange={(event) => setHipaa(event.target.value)}>
              <option value="">{level.kind === "global" ? "Off" : "Same as the level above"}</option>
              <option value="on">On: compliance cards need a reviewer's name</option>
              <option value="off">Off</option>
            </select>
            {!hipaa ? <span className="inherit">{inherited("hipaa")}</span> : null}
          </label>

          <label className="field field-wide">
            <span>Holidays, one date per line</span>
            <textarea
              rows={3}
              value={holidays}
              onChange={(event) => setHolidays(event.target.value)}
              placeholder="2026-12-25"
            />
            <span className="inherit">
              {level.kind === "global"
                ? "Every company and site skips these."
                : inheritedHolidays.length > 0
                  ? `Added to ${inheritedHolidays.length} from above: ${inheritedHolidays
                      .slice(0, 4)
                      .map((day) => dayText(day, true))
                      .join("; ")}${inheritedHolidays.length > 4 ? "…" : ""}`
                  : "Added to any set above. None are, yet."}
            </span>
          </label>
        </fieldset>
        {admin ? (
          <div className="action-row">
            <button className="button" type="submit" disabled={busy}>
              Save {level.name}
            </button>
          </div>
        ) : (
          <p className="muted">Only an admin can change these.</p>
        )}
      </form>
      <datalist id="zones">
        {ZONES.map((zone) => (
          <option key={zone} value={zone} />
        ))}
      </datalist>
    </details>
  );
}

function AddLevel({ companies, busy, run }: Props & { companies: LevelView[] }) {
  const { api } = useOrg();
  const [company, setCompany] = useState("");
  const [site, setSite] = useState("");
  const [parentId, setParentId] = useState("");
  const parent = parentId || companies[0]?.id || "";

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Add a company or a site</h2>
      <form
        className="admin-form"
        onSubmit={async (event) => {
          event.preventDefault();
          if (await run(() => api.createLevel({ kind: "company", name: company }))) setCompany("");
        }}
      >
        <label className="field">
          Company
          <input
            value={company}
            onChange={(event) => setCompany(event.target.value)}
            placeholder="Harbor Health"
          />
        </label>
        <button className="button" type="submit" disabled={busy || !company.trim()}>
          Add company
        </button>
      </form>
      {companies.length > 0 ? (
        <form
          className="admin-form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (await run(() => api.createLevel({ kind: "site", parentId: parent, name: site }))) setSite("");
          }}
        >
          <label className="field">
            Site
            <input value={site} onChange={(event) => setSite(event.target.value)} placeholder="Portland" />
          </label>
          <label className="field">
            Of
            <select value={parent} onChange={(event) => setParentId(event.target.value)}>
              {companies.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
          <button className="button" type="submit" disabled={busy || !site.trim()}>
            Add site
          </button>
        </form>
      ) : (
        <p className="muted">Add a company first; every site belongs to one.</p>
      )}
    </div>
  );
}

/** Who does each department's part at each site: here, and in the setup wizard. */
export function StaffingEditor({
  setup,
  editable,
  busy,
  run,
}: Props & { setup: SetupResponse; editable: boolean }) {
  const { api, people } = useOrg();
  const sites = setup.levels.filter((level) => level.kind === "site");
  const active = people.filter((person) => person.active);

  if (sites.length === 0) return null;

  const options = (department: string) => [
    ...active.filter((person) => person.department === department),
    ...active.filter((person) => person.department !== department),
  ];

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Who does each department's part</h2>
      <p className="muted">
        The default for every onboarding at a site. Nobody set: the whole department hears, and the first to
        pick it up takes it. Changing this does not move cards that already exist.
      </p>
      {sites.map((site, index) => (
        <details key={site.id} className="staffing" open={index === 0}>
          <summary className="level-summary">{site.name}</summary>
          <div className="staffing-rows">
            {(setup.departmentsAt[site.id] ?? setup.departments).map((department) => {
              const now = setup.staffing[site.id]?.[department] ?? { primary: null, backup: null };
              const change = (primary: string | null, backup: string | null) =>
                void run(() => api.setStaffing({ siteId: site.id, department, primary, backup }));
              return (
                <div key={department} className="staffing-row">
                  <span className="staffing-department">{department}</span>
                  <label className="field">
                    <span>Does it</span>
                    <select
                      value={now.primary ?? ""}
                      disabled={!editable || busy}
                      onChange={(event) => change(event.target.value || null, now.backup)}
                    >
                      <option value="">Nobody: the department</option>
                      {options(department).map((person) => (
                        <option key={person.id} value={person.id}>
                          {person.name || person.email}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>Backup</span>
                    <select
                      value={now.backup ?? ""}
                      disabled={!editable || busy}
                      onChange={(event) => change(now.primary, event.target.value || null)}
                    >
                      <option value="">None</option>
                      {options(department).map((person) => (
                        <option key={person.id} value={person.id}>
                          {person.name || person.email}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              );
            })}
          </div>
        </details>
      ))}
    </div>
  );
}

/** Stripe's subscription statuses, in words, beside the colour (PRD 10.7). */
const STATUS_WORDS: Record<string, string> = {
  active: "Paid",
  trialing: "On trial",
  past_due: "Payment late",
  unpaid: "Unpaid",
  incomplete: "Payment not finished",
  paused: "Paused",
};

/**
 * The organisation's plan, and paying for it (PRD 13.2): a Stripe Checkout
 * for a paid plan, or Stripe's own portal once it pays, where the plan is
 * changed, the card updated and the subscription cancelled. What the plan is
 * stays the webhook's to say, so coming back from Stripe says it may take a
 * moment rather than pretending it already changed.
 */
function Billing() {
  const { api, org } = useOrg();
  const { data, error } = useLoad(() => api.billing(), [api]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [paid] = useState(() => new URLSearchParams(window.location.search).has("paid"));

  async function go(work: () => Promise<{ url: string }>) {
    setBusy(true);
    setProblem(null);
    try {
      window.location.assign((await work()).url);
    } catch (failure) {
      setProblem(sentence(failure));
      setBusy(false);
    }
  }

  if (!data) return error ? <Problem>{error}</Problem> : null;
  const plan = planOf(data.plan);
  const status = data.status && data.subscribed ? (STATUS_WORDS[data.status] ?? data.status) : null;
  const late = data.status === "past_due" || data.status === "unpaid" || data.status === "incomplete";

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Plan</h2>
      <p>
        {org.name} is on <strong>{plan?.name ?? data.plan}</strong>
        {plan?.price ? `, $${plan.price} a month` : plan ? ", free" : ""}.{" "}
        {status ? <span className={`pill ${late ? "s-warn" : "s-sent"}`}>{status}</span> : null}
      </p>
      {plan && data.use ? <PlanUseRows plan={plan} use={data.use} /> : null}
      {paid ? (
        <p className="notice-line" role="status">
          Thank you. Stripe is confirming the payment, and the plan changes here within a minute; reload to
          see it.
        </p>
      ) : null}
      {!data.available ? (
        <p className="muted">To change plan, write to utuwrk and it will be set for you.</p>
      ) : data.subscribed ? (
        <>
          <p className="muted">Change plan, update the card, see invoices or cancel, on Stripe.</p>
          <div className="action-row">
            <button
              className="button"
              type="button"
              disabled={busy}
              onClick={() => void go(() => api.billingPortal())}
            >
              Manage billing
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="muted">
            Pay by card, monthly, through Stripe. Cancel any time and you go back to Free.
          </p>
          <div className="action-row">
            {PAID_PLANS.map((id) => (
              <button
                key={id}
                className={id === "growth" ? "button" : "button button-quiet"}
                type="button"
                disabled={busy}
                onClick={() => void go(() => api.checkout(id))}
              >
                {PLANS[id].name}, ${PLANS[id].price} a month
              </button>
            ))}
            {data.portal ? (
              <button
                className="button button-quiet"
                type="button"
                disabled={busy}
                onClick={() => void go(() => api.billingPortal())}
              >
                Past invoices
              </button>
            ) : null}
          </div>
        </>
      )}
      <Problem>{problem}</Problem>
    </div>
  );
}

/** How much of each limit the organisation has used, as a bar and a percent, the way Mail shows its pool. */
function PlanUseRows({ plan, use }: { plan: Plan; use: PlanUse }) {
  const count = (n: number) => n.toLocaleString();
  const rows: { label: string; used: number; most: number | null }[] = [
    { label: "People", used: use.people, most: plan.headcount },
    { label: "Sites", used: use.sites, most: plan.sites },
    { label: "Your templates", used: use.templates, most: plan.templates },
  ];
  if (!plan.features.includes("simultaneous-work"))
    rows.push({ label: "Onboardings and offboardings open", used: use.inFlight, most: 1 });

  return (
    <div className="plan-use">
      {rows.map((row) => {
        const share = row.most === null ? null : Math.min(100, Math.round((row.used / row.most) * 100));
        return (
          <div key={row.label} className="plan-use-row">
            <p className="plan-use-line">
              <span>{row.label}</span>
              <span className="muted">
                {row.most === null
                  ? `${count(row.used)}, no limit`
                  : `${count(row.used)} of ${count(row.most)}, ${share}%`}
              </span>
            </p>
            {row.most !== null && share !== null ? (
              <progress
                className={share >= 90 ? "progress progress-near" : "progress"}
                max={row.most}
                value={Math.min(row.used, row.most)}
                aria-label={`${row.label}: ${row.used} of ${row.most}`}
              />
            ) : null}
          </div>
        );
      })}
      <p className="muted">
        People are those under management, not counting anyone who has left. Adding past a limit is refused;
        an offboarding never is.
      </p>
    </div>
  );
}

/**
 * The organisation's own mail pool: how much it may send an hour, what it has
 * used, and asking utuwrk for more. A notice past the allowance is still in
 * the app; only its mail waits, which is what "held back" counts.
 */
function MailPool() {
  const { api } = useOrg();
  const { data, setData, error } = useLoad(() => api.mailUsage(), [api]);
  const [asking, setAsking] = useState(false);
  const [perHour, setPerHour] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      setData(await api.requestMail({ perHour: Number(perHour), reason }));
      setAsking(false);
      setPerHour("");
      setReason("");
    } catch (failure) {
      setProblem(sentence(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!data) return error ? <Problem>{error}</Problem> : null;
  const count = (n: number) => n.toLocaleString();
  const open = data.request?.status === "open" ? data.request : null;
  const last = data.request && data.request.status !== "open" ? data.request : null;

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Mail</h2>
      <p className="muted">
        {count(data.perHour)} mails an hour
        {data.perHour === data.fromPlan
          ? ", your plan's allowance"
          : `, raised from your plan's ${count(data.fromPlan)}`}
        . Past it, notices still appear in utuwrk; only their mail waits.
      </p>
      <progress
        className="progress"
        max={data.perHour}
        value={Math.min(data.lastHour, data.perHour)}
        aria-label={`${data.lastHour} of ${data.perHour} mails sent in the last hour`}
      />
      <p className="card-meta">
        <span>{count(data.lastHour)} in the last hour</span>
        <span>Busiest hour this week: {count(data.busiestHour)}</span>
        <span>Held back this week: {count(data.heldThisWeek)}</span>
      </p>
      {open ? (
        <p className="notice-line" role="status">
          You asked for {count(open.perHour)} an hour on{" "}
          {new Date(open.createdAt * 1000).toLocaleDateString()}. utuwrk will decide soon.
        </p>
      ) : last ? (
        <p className={`notice-line${last.status === "declined" ? " warn" : ""}`}>
          Your request for {count(last.perHour)} an hour was{" "}
          {last.status === "granted" ? "granted" : "declined; ask us why, or ask again with a reason"}.
        </p>
      ) : null}
      {!open && !asking ? (
        <div className="action-row">
          <button className="button button-quiet" type="button" onClick={() => setAsking(true)}>
            Ask for more
          </button>
        </div>
      ) : null}
      {asking ? (
        <form className="admin-form" onSubmit={submit}>
          <label className="field">
            Mails an hour
            <input
              type="number"
              min={data.perHour + 1}
              max={data.most}
              step={1}
              value={perHour}
              onChange={(event) => setPerHour(event.target.value)}
            />
          </label>
          <label className="field">
            Why (optional)
            <input
              value={reason}
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Forty interns start on the same Monday"
            />
          </label>
          <button className="button" type="submit" disabled={busy || !/^\d+$/.test(perHour)}>
            Ask
          </button>
          <button className="button button-quiet" type="button" onClick={() => setAsking(false)}>
            Cancel
          </button>
        </form>
      ) : null}
      <Problem>{problem}</Problem>
    </div>
  );
}
